import { classnames } from 'Utils/classnames/classnames.utils';
import { useRouter } from 'next/router';
import React, { Fragment } from 'react';
import type { LineProps } from 'Cms/product-accommodations/resolver';
import 'Cms/product-accommodations/resolver';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import {
  groupAccommodationsByCategory,
  getTabsItemsAndPropsFromAccommodations,
} from 'Components/lines/product-accommodations-line/productAccommodations.utils';
import { useAppContext } from 'Context/ReactAppContext';
import { getOpenLayersFromLocation } from 'Utils/uri/uriUtils';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { Introduction } from 'Components/frameworks/introduction/Introduction';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { Tabs } from 'Components/frameworks/tabs/Tabs';
type BELineProps = LineProps & {
  accommodationCategory?: {
    rankedCategories?: Array<{
      id?: string;
      ranking?: string;
      price?: {
        totalWithoutDiscount?: FormattedPrice;
        total?: FormattedPrice;
        diffPrice?: string;
      };
    }>;
  };
};
export const ProductAccommodationsLine = (props: BELineProps) => {
  const appContext = useAppContext();
  const { asPath } = useRouter();
  const { isTablet } = useResponsive();
  const {
    config: { common: { loadLess, scrollBackward, scrollForward, viewMore } = {} } = {},
    id,
    isLineExclusiveCollection = false,
    resort: { accommodationsIntroduction },
  } = props;
  const queryLayer = isLineExclusiveCollection ? 'accommodationLayerEC' : 'accommodationLayer';
  const initialSelectedItem = getOpenLayersFromLocation(asPath, queryLayer)[0];

  if (!props.config || !props.resort) {
    return null;
  }

  const accommodationCategories = groupAccommodationsByCategory(props);
  const { items, slot } = getTabsItemsAndPropsFromAccommodations(props, accommodationCategories);
  const displayRoomsComparator =
    isFeatureFlipped(appContext)('displayRoomsComparator') &&
    !isLineExclusiveCollection &&
    accommodationCategories.length > 1;
  const content = (
    <Fragment>
      {!isLineExclusiveCollection && accommodationsIntroduction && (
        <SiteContainer isWide={isTablet}>
          <Introduction
            className="mb-50 mt-30 text-center"
            dataTestId="Line-Product-Accommodations__Introduction"
            isTextSeeMoreCollapseEnabled
            seeMoreLabel={viewMore}
            seeLessLabel={loadLess}
            text={accommodationsIntroduction.description}
            theme="column"
            title={accommodationsIntroduction.shortDescription}
          />
        </SiteContainer>
      )}
      <Tabs
        hasControlsOnTouch
        id="product-accommodations-line-tabs"
        initialSelectedItem={initialSelectedItem}
        isContentUnmountedOnCollapse
        isScrollable
        items={items}
        scrollBackwardLabel={scrollBackward}
        scrollForwardLabel={scrollForward}
        slotRight={displayRoomsComparator ? (activeIndexes) => slot(activeIndexes) : null}
      />
    </Fragment>
  );
  return (
    <div
      className={classnames(
        'Line',
        'max-w-1220',
        'md:site-container md:relative md:mx-auto md:w-full md:px-20',
      )}
      id={id}
    >
      {content}
    </div>
  );
};
